import { useRef, useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useVoice, GeminiVoiceId, VadMode } from '../contexts/voice';
import { useTheme } from '../contexts/ThemeContext';
import { useTutorial } from '../contexts/TutorialContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  VoiceStatusIndicator,
  VoiceMicButton,
  PTTToggleButton,
  VoiceChatHistory,
  VoiceTextInput,
  VoiceSelector,
  VoiceHeader,
  PTTSuggestionPopup,
  PTTHintBanner,
  VadModeSelector,
} from './voice';

const PTT_SUGGESTION_KEY = 'voice_ptt_suggestion_seen';

interface VoiceOverlayProps {
  expanded?: boolean;
  onToggleExpand?: () => void;
}

export default function VoiceOverlay({ expanded = false, onToggleExpand }: VoiceOverlayProps) {
  const {
    state,
    chatMode,
    messages,
    currentUserText,
    currentAssistantText,
    error,
    muted,
    isTyping,
    currentAgent,
    pushToTalkMode,
    audioDetectedWhileMuted,
    connect,
    disconnect,
    closeChat,
    sendContext,
    sendTextMessage,
    toggleMute,
    setPushToTalkMode,
    startTalking,
    stopTalking,
    selectedVoice,
    setSelectedVoice,
    vadMode,
    setVadMode,
  } = useVoice();
  const { isAdmin } = useTheme();
  const { isActive: tutorialActive, currentStep } = useTutorial();
  const { t } = useLanguage();

  // Voice selector state
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);

  // First-time PTT suggestion popup
  const [showPTTSuggestion, setShowPTTSuggestion] = useState(false);

  // PTT state - use ref to persist across re-renders
  const isSpaceDownRef = useRef(false);
  const startTalkingRef = useRef(startTalking);
  const stopTalkingRef = useRef(stopTalking);

  // Keep refs in sync with props
  useEffect(() => {
    startTalkingRef.current = startTalking;
    stopTalkingRef.current = stopTalking;
  }, [startTalking, stopTalking]);

  // Keyboard push-to-talk (spacebar)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;

      e.preventDefault();
      if (e.repeat || isSpaceDownRef.current) return;

      isSpaceDownRef.current = true;
      console.log('[PTT] Space DOWN');
      startTalkingRef.current();
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      e.preventDefault();

      if (!isSpaceDownRef.current) return;

      isSpaceDownRef.current = false;
      console.log('[PTT] Space UP');
      stopTalkingRef.current();
    };

    const handleStop = () => {
      if (isSpaceDownRef.current) {
        console.log('[PTT] Window lost focus - stop');
        isSpaceDownRef.current = false;
        stopTalkingRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleStop);
    document.addEventListener('visibilitychange', handleStop);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleStop);
      document.removeEventListener('visibilitychange', handleStop);
    };
  }, []);

  // Block disconnect during active tutorial steps
  const blockDisconnect = tutorialActive && !['welcome', 'complete'].includes(currentStep);

  const getStatusText = () => {
    switch (state) {
      case 'connecting':
        return t('voice.connecting');
      case 'connected':
        if (pushToTalkMode && muted) {
          return t('voice.hold_to_talk');
        }
        return t('voice.listening');
      case 'speaking':
        return t('voice.you_speaking');
      case 'listening':
        return t('voice.speaking');
      default:
        return t('voice.click_to_start');
    }
  };

  const buildChatSummary = () => {
    if (messages.length === 0) return '';
    const recentMessages = messages.slice(-6);
    const summary = recentMessages
      .map(m => `${m.role === 'user' ? 'Klant' : 'Sophie'}: ${m.content.slice(0, 100)}${m.content.length > 100 ? '...' : ''}`)
      .join('\n');
    return `[Eerder besproken in chat]\n${summary}`;
  };

  const handleVoiceClick = () => {
    console.log('[VoiceOverlay] Voice click - state:', state);
    if (state === 'disconnected') {
      const hasSeenSuggestion = localStorage.getItem(PTT_SUGGESTION_KEY);
      if (!hasSeenSuggestion) {
        setShowPTTSuggestion(true);
        return;
      }
      connect('voice');
    } else {
      if (blockDisconnect) return;
      disconnect();
    }
  };

  const handlePTTChoice = (usePTT: boolean) => {
    localStorage.setItem(PTT_SUGGESTION_KEY, 'true');
    setShowPTTSuggestion(false);
    if (usePTT) {
      setPushToTalkMode(true);
    }
    connect('voice');
  };

  const handleTextClick = () => {
    console.log('[VoiceOverlay] Text click - state:', state);
    if (state === 'disconnected') {
      connect('text');
      if (onToggleExpand && !expanded) {
        onToggleExpand();
      }
    } else {
      if (blockDisconnect) return;
      disconnect();
    }
  };

  const handleSwitchToVoice = () => {
    const chatSummary = buildChatSummary();
    disconnect();
    setTimeout(() => {
      connect('voice');
      if (chatSummary) {
        setTimeout(() => {
          sendContext({
            page: 'store',
            cart_count: 0,
            chat_history: chatSummary,
          } as any);
        }, 500);
      }
    }, 100);
  };

  const zIndex = tutorialActive ? 'z-[101]' : 'z-50';

  // PTT Suggestion Popup
  if (showPTTSuggestion) {
    return (
      <PTTSuggestionPopup
        onChoosePTT={() => handlePTTChoice(true)}
        onChooseOpenMic={() => handlePTTChoice(false)}
        translations={{
          noisy_environment: t('voice.noisy_environment'),
          noisy_hint: t('voice.noisy_hint'),
          use_ptt: t('voice.use_ptt'),
          open_mic: t('voice.open_mic'),
        }}
      />
    );
  }

  // Compact mode - floating buttons
  if (!expanded) {
    // Text mode active: Show chat button + voice button option
    if (state !== 'disconnected' && chatMode === 'text') {
      return (
        <div className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 ${zIndex} flex gap-3`}>
          <button
            onClick={onToggleExpand}
            className="p-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg transition-all duration-300 transform hover:scale-110 active:scale-95"
            title="Open chat"
          >
            <MessageSquare className="w-6 h-6 text-white" />
          </button>
          <VoiceMicButton
            state={state}
            muted={muted}
            variant="primary"
            size="lg"
            onClick={handleSwitchToVoice}
          />
        </div>
      );
    }

    // Voice mode active: Show voice controls
    if (state !== 'disconnected' && chatMode === 'voice') {
      return (
        <>
          <PTTHintBanner show={pushToTalkMode && audioDetectedWhileMuted} />
          <div className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 ${zIndex} flex flex-col items-end gap-3`}>
            <VoiceStatusIndicator
              state={state}
              currentAgent={currentAgent}
              statusText={getStatusText()}
            />

            <PTTToggleButton
              enabled={pushToTalkMode}
              onClick={() => setPushToTalkMode(!pushToTalkMode)}
            />

            {pushToTalkMode ? (
              <VoiceMicButton
                state={state}
                muted={muted}
                variant="ptt"
                size="lg"
                onMouseDown={startTalking}
                onMouseUp={stopTalking}
                onMouseLeave={stopTalking}
                onTouchStart={startTalking}
                onTouchEnd={stopTalking}
              />
            ) : (
              <VoiceMicButton
                state={state}
                muted={muted}
                variant="mute"
                size="md"
                onClick={toggleMute}
              />
            )}

            <button
              onClick={onToggleExpand}
              className="p-3 rounded-full bg-card border border-primary/20 text-foreground shadow-lg hover:bg-card/80 transition-all"
              title="Toon chat"
            >
              <MessageSquare className="w-5 h-5" />
            </button>

            <VoiceMicButton
              state={state}
              muted={muted}
              variant="stop"
              size="lg"
              onClick={handleVoiceClick}
            />
          </div>
        </>
      );
    }

    // Disconnected: Show two buttons + voice selector + VAD mode (admin)
    return (
      <div className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 ${zIndex} flex flex-col items-end gap-3`}>
        <VoiceSelector
          selectedVoice={selectedVoice}
          onSelect={(voice) => setSelectedVoice(voice as GeminiVoiceId)}
          isOpen={showVoiceSelector}
          onToggle={() => setShowVoiceSelector(!showVoiceSelector)}
          onClose={() => setShowVoiceSelector(false)}
        />

        {isAdmin && (
          <VadModeSelector
            vadMode={vadMode}
            onSelect={(mode) => setVadMode(mode as VadMode)}
          />
        )}

        <div className="flex gap-3 items-center">
          <VoiceMicButton
            state={state}
            muted={muted}
            variant="text"
            size="lg"
            onClick={handleTextClick}
          />
          <VoiceMicButton
            state={state}
            muted={muted}
            variant="primary"
            size="lg"
            onClick={handleVoiceClick}
          />
        </div>
      </div>
    );
  }

  // Expanded mode - full chat panel
  return (
    <div className={`fixed bottom-0 right-0 sm:bottom-6 sm:right-6 ${zIndex} w-full sm:w-96 max-h-[85vh] sm:max-h-[70vh] flex flex-col bg-card/95 backdrop-blur-sm border-t sm:border border-primary/20 sm:rounded-2xl shadow-2xl animate-fade-in overflow-hidden`}>
      <VoiceHeader
        currentAgent={currentAgent}
        statusText={getStatusText()}
        chatMode={chatMode}
        state={state}
        hasMessages={messages.length > 0}
        onSwitchToVoice={handleSwitchToVoice}
        onCloseChat={closeChat}
        onCollapse={onToggleExpand!}
      />

      <VoiceChatHistory
        messages={messages}
        currentUserText={currentUserText}
        currentAssistantText={currentAssistantText}
        state={state}
        isTyping={isTyping}
        chatMode={chatMode}
        emptyStateText={t('voice.click_mic_to_start')}
        className="flex-1 min-h-[200px] max-h-[400px]"
      />

      {/* Controls */}
      <div className="border-t border-primary/10 bg-card/50 p-3">
        {state !== 'disconnected' && chatMode === 'text' && (
          <VoiceTextInput
            onSend={sendTextMessage}
            placeholder={t('voice.type_message')}
            autoFocus={true}
            className="mb-3"
          />
        )}

        <div className="flex items-center justify-center gap-3">
          {state !== 'disconnected' && chatMode === 'voice' && (
            <>
              <PTTToggleButton
                enabled={pushToTalkMode}
                onClick={() => setPushToTalkMode(!pushToTalkMode)}
              />

              {pushToTalkMode ? (
                <VoiceMicButton
                  state={state}
                  muted={muted}
                  variant="ptt"
                  size="md"
                  onMouseDown={startTalking}
                  onMouseUp={stopTalking}
                  onMouseLeave={stopTalking}
                  onTouchStart={startTalking}
                  onTouchEnd={stopTalking}
                />
              ) : (
                <VoiceMicButton
                  state={state}
                  muted={muted}
                  variant="mute"
                  size="sm"
                  onClick={toggleMute}
                />
              )}
            </>
          )}

          {state === 'disconnected' ? (
            <div className="flex gap-2">
              <VoiceMicButton
                state={state}
                muted={muted}
                variant="text"
                size="md"
                onClick={handleTextClick}
              />
              <VoiceMicButton
                state={state}
                muted={muted}
                variant="primary"
                size="md"
                onClick={handleVoiceClick}
              />
            </div>
          ) : chatMode === 'voice' ? (
            <VoiceMicButton
              state={state}
              muted={muted}
              variant="stop"
              size="md"
              onClick={handleVoiceClick}
            />
          ) : null}
        </div>

        {error && (
          <div className="mt-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
            <p className="text-red-500 text-xs">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
